import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import "./_toast.scss";
import "./_error-icon.scss";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

// Error icon (static)
const ErrorIcon = () => <div className="error-icon" />;

// Success animation icon
const SuccessIcon = () => (
  <div style={{ height: "24px", width: "24px" }}>
    <DotLottieReact
      src="https://lottie.host/9b300239-3aa4-4eb1-aab9-b4af3c593a7a/J0CCgqk95z.lottie"
      loop
      autoplay
    />
  </div>
);

const WarningIcon = () => (
  <div style={{ height: "24px", width: "24px" }}>
    <DotLottieReact
      src="https://lottie.host/af0abadf-7fde-47bb-a86a-82b77f24811e/qaqUyPMBa7.lottie"
      loop
      autoplay
    />
  </div>
);

// Processing spinner icon (replace with your preferred spinner .lottie if needed)
const ProcessingIcon = () => (
  <svg
    className="spinner-svg"
    width="12"
    height="12"
    viewBox="0 0 50 50"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      className="spinner-path"
      cx="25"
      cy="25"
      r="20"
      fill="none"
      strokeWidth="5"
    />
  </svg>
);

const ToastContext = createContext();

export const useToast = () => useContext(ToastContext);

export const ToastProvider = ({ children }) => {
  const [toast, setToast] = useState(null);
  const timeoutRef = useRef(null);
  const exitTimeoutRef = useRef(null);

  // Hover pause/resume tracking
  const VISIBLE_DURATION_MS = 6000;
  const EXIT_ANIMATION_MS = 300;
  const remainingMsRef = useRef(VISIBLE_DURATION_MS);
  const dueAtRef = useRef(null);

  const clearTimers = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (exitTimeoutRef.current) clearTimeout(exitTimeoutRef.current);
    timeoutRef.current = null;
    exitTimeoutRef.current = null;
  };

  const startExit = () => {
    setToast((prev) => prev && { ...prev, exiting: true });
    exitTimeoutRef.current = setTimeout(() => {
      setToast(null);
    }, EXIT_ANIMATION_MS);
  };

  const showToast = useCallback((message, type = "success") => {
    // Clear existing timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (exitTimeoutRef.current) clearTimeout(exitTimeoutRef.current);

    setToast({ message, type });

    // Skip auto-dismiss for "processing" toasts
    if (type !== "processing") {
      remainingMsRef.current = VISIBLE_DURATION_MS;
      dueAtRef.current = Date.now() + remainingMsRef.current;
      timeoutRef.current = setTimeout(() => {
        startExit();
      }, remainingMsRef.current);
    }
  }, []);

  const hideToast = useCallback(() => {
    setToast((prev) => prev && { ...prev, exiting: true });
    exitTimeoutRef.current = setTimeout(() => {
      setToast(null);
    }, EXIT_ANIMATION_MS);
  }, []);

  const handleMouseEnter = () => {
    if (!toast) return;
    // Compute remaining time and clear timers
    if (dueAtRef.current) {
      remainingMsRef.current = Math.max(0, dueAtRef.current - Date.now());
    }
    clearTimers();
  };

  const handleMouseLeave = () => {
    if (!toast) return;
    if (toast.type === "processing") return;
    // Resume countdown with remaining time
    if (remainingMsRef.current > 0) {
      dueAtRef.current = Date.now() + remainingMsRef.current;
      timeoutRef.current = setTimeout(() => {
        startExit();
      }, remainingMsRef.current);
    } else {
      // If time already elapsed, exit immediately
      startExit();
    }
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          exiting={toast.exiting}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      )}
    </ToastContext.Provider>
  );
};

const Toast = ({ message, type, exiting, onMouseEnter, onMouseLeave }) => {
  return (
    <div
      className={`toast toast-${type} ${exiting ? "toast-exit" : ""}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {type === "error" && <ErrorIcon />}
      {type === "success" && <SuccessIcon />}
      {type === "processing" && <ProcessingIcon />}
      {type === "warning" && <WarningIcon />}
      <span>{message}</span>
    </div>
  );
};
